import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { API_CONSTANTS, apiUtils } from '../../utils/api';
import { toast } from 'react-toastify';
import 'react-datepicker/dist/react-datepicker.css';
import './PlacementForm.css';

const PlacementForm = ({ placement, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    application_status: 'Applied',
    position_role: '',
    salary_package: '',
    application_date: new Date(),
    online_assessment_date: null,
    interview_dates: [],
    result_date: null,
    notes_comments: '',
    priority_rating: null,
    team_information: '',
    season: '2025 Placement',
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form with placement data if editing
  useEffect(() => {
    if (placement) {
      setFormData({
        company_name: placement.company_name || '',
        application_status: placement.application_status || 'Applied',
        position_role: placement.position_role || '',
        salary_package: placement.salary_package || '',
        application_date: placement.application_date ? new Date(placement.application_date) : new Date(),
        online_assessment_date: placement.online_assessment_date ? new Date(placement.online_assessment_date) : null,
        interview_dates: placement.interview_dates ? placement.interview_dates.map(date => new Date(date)) : [],
        result_date: placement.result_date ? new Date(placement.result_date) : null,
        notes_comments: placement.notes_comments || '',
        priority_rating: placement.priority_rating || null,
        team_information: placement.team_information || '',
        season: placement.season || '2025 Placement',
      });
    }
  }, [placement]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date,
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleInterviewDateAdd = () => {
    setFormData(prev => ({
      ...prev,
      interview_dates: [...prev.interview_dates, new Date()],
    }));
  };

  const handleInterviewDateChange = (index, date) => {
    setFormData(prev => ({
      ...prev,
      interview_dates: prev.interview_dates.map((d, i) => i === index ? date : d),
    }));
  };

  const handleInterviewDateRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      interview_dates: prev.interview_dates.filter((_, i) => i !== index),
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : null,
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }

    if (!formData.position_role.trim()) {
      newErrors.position_role = 'Position role is required';
    }

    if (!formData.application_date) {
      newErrors.application_date = 'Application date is required';
    }

    if (!formData.application_status) {
      newErrors.application_status = 'Application status is required';
    }

    if (!formData.season) {
      newErrors.season = 'Season is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    setLoading(true);

    try {
      // Format data for API
      const apiData = {
        ...formData,
        application_date: apiUtils.formatDateForAPI(formData.application_date),
        online_assessment_date: apiUtils.formatDateForAPI(formData.online_assessment_date),
        interview_dates: formData.interview_dates.map(date => apiUtils.formatDateForAPI(date)),
        result_date: apiUtils.formatDateForAPI(formData.result_date),
      };

      const result = await onSave(placement?.id, apiData);
      
      if (result.success) {
        onClose();
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = API_CONSTANTS.APPLICATION_STATUSES.map(status => ({
    value: status,
    label: status,
  }));

  const seasonOptions = API_CONSTANTS.SEASONS.map(season => ({
    value: season,
    label: season,
  }));

  const priorityOptions = API_CONSTANTS.PRIORITY_RATINGS;

  return (
    <div className="placement-form-overlay">
      <div className="placement-form-modal">
        <div className="form-header">
          <h3 className="form-title">
            <i className="fas fa-briefcase me-2"></i>
            {placement ? 'Edit Placement' : 'Add New Placement'}
          </h3>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            disabled={loading}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="placement-form">
          <div className="form-body">
            {/* Basic Information */}
            <div className="form-section">
              <h4 className="section-title">Basic Information</h4>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="company_name" className="form-label required">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company_name"
                      name="company_name"
                      className={`form-control ${errors.company_name ? 'is-invalid' : ''}`}
                      value={formData.company_name}
                      onChange={handleInputChange}
                      placeholder="Enter company name"
                      disabled={loading}
                      required
                    />
                    {errors.company_name && (
                      <div className="invalid-feedback">{errors.company_name}</div>
                    )}
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="position_role" className="form-label required">
                      Position Role
                    </label>
                    <input
                      type="text"
                      id="position_role"
                      name="position_role"
                      className={`form-control ${errors.position_role ? 'is-invalid' : ''}`}
                      value={formData.position_role}
                      onChange={handleInputChange}
                      placeholder="e.g., Software Engineer"
                      disabled={loading}
                      required
                    />
                    {errors.position_role && (
                      <div className="invalid-feedback">{errors.position_role}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label required">Application Status</label>
                    <Select
                      options={statusOptions}
                      value={statusOptions.find(option => option.value === formData.application_status)}
                      onChange={(option) => handleSelectChange('application_status', option)}
                      placeholder="Select status"
                      isDisabled={loading}
                      className={errors.application_status ? 'is-invalid' : ''}
                    />
                    {errors.application_status && (
                      <div className="invalid-feedback d-block">{errors.application_status}</div>
                    )}
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label required">Season</label>
                    <Select
                      options={seasonOptions}
                      value={seasonOptions.find(option => option.value === formData.season)}
                      onChange={(option) => handleSelectChange('season', option)}
                      placeholder="Select season"
                      isDisabled={loading}
                      className={errors.season ? 'is-invalid' : ''}
                    />
                    {errors.season && (
                      <div className="invalid-feedback d-block">{errors.season}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="salary_package" className="form-label">
                      Salary Package
                    </label>
                    <input
                      type="text"
                      id="salary_package"
                      name="salary_package"
                      className="form-control"
                      value={formData.salary_package}
                      onChange={handleInputChange}
                      placeholder="e.g., 80k, $120k"
                      disabled={loading}
                    />
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Priority Rating</label>
                    <Select
                      options={priorityOptions}
                      value={priorityOptions.find(option => option.value === formData.priority_rating)}
                      onChange={(option) => handleSelectChange('priority_rating', option)}
                      placeholder="Select priority"
                      isDisabled={loading}
                      isClearable
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="form-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {placement ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  {placement ? 'Update Placement' : 'Create Placement'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlacementForm;
